<template>
  <div class="envio-form-page">
    <div class="form-hero">
      <p class="form-crumb">Logística / Nuevo Servicio</p>
      <h1 class="form-title">Crear Nuevo Servicio</h1>
      <p class="form-subtitle">
        Complete la información para registrar un nuevo servicio en el sistema.
      </p>
    </div>

    <div class="form-card">
      <v-form @submit.prevent="submit">
        <!-- ===== Tipo de servicio ===== -->
        <section class="form-section">
          <h3 class="form-section__title">Tipo de servicio</h3>
          <div class="type-toggle">
            <button
              type="button"
              class="type-card"
              :class="{
                'type-card--active': assignmentType === 'transportista',
              }"
              @click="assignmentType = 'transportista'"
            >
              <v-icon class="type-card__icon">local_shipping</v-icon>
              <span class="type-card__label">Transportista</span>
              <span class="type-card__hint">Formulario completo</span>
            </button>
            <button
              type="button"
              class="type-card"
              :class="{ 'type-card--active': assignmentType === 'mensajero' }"
              @click="assignmentType = 'mensajero'"
            >
              <v-icon class="type-card__icon">directions_bike</v-icon>
              <span class="type-card__label">Mensajería</span>
              <span class="type-card__hint">Datos básicos</span>
            </button>
          </div>
        </section>

        <section class="form-section">
          <h3 class="form-section__title">Bitácora del servicio</h3>
          <v-select
            v-model="form.bitacora_template_id"
            :items="templateItems"
            :loading="loadingTemplates"
            label="Tipo de gestión (obligatorio)"
            solo
            :disabled="loadingTemplates"
            no-data-text="No hay bitácoras activas"
          ></v-select>
          <p v-if="templateError" role="alert">{{ templateError }}</p>
          <v-btn v-if="templateError" flat small @click="loadTemplates"
            >Reintentar carga</v-btn
          >
          <ol v-if="selectedTemplate" class="template-preview">
            <li v-for="step in selectedTemplate.steps" :key="step.id">
              {{ step.title }}
              <span v-if="step.requiresPhoto"> · Foto obligatoria</span>
              <span v-if="step.requiresComment"> · Comentario obligatorio</span>
              <span v-if="step.requiresSignature"> · Firma obligatoria</span>
              <span v-if="step.requiresReceiverName"> · Nombre y apellido</span>
            </li>
          </ol>
        </section>
        <div class="form-divider"></div>

        <!-- ===== Información General ===== -->
        <section class="form-section">
          <h3 class="form-section__title">
            {{
              assignmentType === 'mensajero'
                ? 'Información del Servicio'
                : 'Información General'
            }}
          </h3>
          <div class="form-grid" v-if="assignmentType === 'transportista'">
            <div class="form-field">
              <label
                class="form-label ip-trigger"
                @mouseenter="onRefLabelHover"
                @mouseleave="onRefLabelLeave"
              >
                Número de Referencia
                <span class="ip-trigger__badge">
                  <v-icon small class="ip-trigger__icon"
                    >assignment_turned_in</v-icon
                  >
                  Ver facturas
                </span>
              </label>
              <v-text-field
                v-model="form.reference_number"
                name="reference_number"
                placeholder="REF-001"
                solo
                class="form-input"
              ></v-text-field>
            </div>
            <div class="form-field">
              <label class="form-label">Número de Factura</label>
              <v-text-field
                v-model="form.invoice_number"
                name="invoice_number"
                placeholder="FAC-2026-001"
                solo
                class="form-input"
                :class="{ 'form-input--locked': invoicePicker.locked }"
                :readonly="invoicePicker.locked"
              ></v-text-field>
            </div>
          </div>
          <v-select
            v-model="form.distributor_id"
            :items="invoicePicker.distributors"
            item-text="name"
            item-value="id"
            label="Distribuidor del servicio"
            hint="Permite incluir el servicio en los indicadores de ese distribuidor."
            persistent-hint
            clearable
            :loading="invoicePicker.loadingDist"
            :disabled="invoicePicker.locked"
          ></v-select>
          <div
            class="form-grid form-grid--3"
            v-if="assignmentType === 'transportista'"
          >
            <div class="form-field">
              <label class="form-label">Prioridad</label>
              <v-select
                v-model="form.priority"
                :items="priorityOptions"
                solo
                class="form-input"
                hide-details
                no-data-text="Sin opciones"
              ></v-select>
            </div>
            <div class="form-field">
              <label class="form-label"
                >Fecha de Entrada <span class="form-required">*</span></label
              >
              <v-text-field
                v-model="form.entry_date"
                v-validate="'required'"
                name="entry_date"
                type="date"
                solo
                class="form-input"
                :class="{ 'form-input--locked': invoicePicker.locked }"
                :readonly="invoicePicker.locked"
                :error-messages="errors.collect('entry_date')"
              ></v-text-field>
            </div>
            <div class="form-field">
              <label class="form-label">Fecha Estimada de Entrega</label>
              <v-text-field
                v-model="form.estimated_delivery_date"
                name="estimated_delivery_date"
                type="date"
                solo
                class="form-input"
              ></v-text-field>
            </div>
          </div>
          <div class="form-grid">
            <div class="form-field form-field--full">
              <label class="form-label">
                {{
                  assignmentType === 'mensajero'
                    ? '¿Qué va a buscar?'
                    : 'Descripción'
                }}
                <span
                  v-if="assignmentType === 'mensajero'"
                  class="form-required"
                  >*</span
                >
              </label>
              <v-text-field
                v-model="form.description"
                v-validate="assignmentType === 'mensajero' ? 'required' : ''"
                name="description"
                :placeholder="
                  assignmentType === 'mensajero'
                    ? 'Ej. Recoger documentos / paquete a entregar'
                    : 'Descripción del contenido del servicio'
                "
                solo
                class="form-input"
                :error-messages="errors.collect('description')"
              ></v-text-field>
            </div>
            <template v-if="assignmentType === 'transportista'">
              <div class="form-field">
                <label class="form-label">Peso (kg)</label>
                <v-text-field
                  v-model="form.weight"
                  name="weight"
                  type="number"
                  step="0.1"
                  placeholder="0.0"
                  solo
                  class="form-input"
                ></v-text-field>
              </div>
              <div class="form-field">
                <label class="form-label">Volumen (m³)</label>
                <v-text-field
                  v-model="form.volume"
                  name="volume"
                  type="number"
                  step="0.001"
                  placeholder="0.000"
                  solo
                  class="form-input"
                ></v-text-field>
              </div>
              <div class="form-field form-field--full">
                <label class="form-label">Observaciones</label>
                <v-textarea
                  v-model="form.observations"
                  name="observations"
                  placeholder="Observaciones adicionales..."
                  solo
                  rows="3"
                  class="form-input"
                ></v-textarea>
              </div>
            </template>
          </div>
        </section>

        <div class="form-divider"></div>

        <!-- ===== Información del Cliente ===== -->
        <section class="form-section">
          <h3 class="form-section__title">Información del Cliente</h3>
          <div class="form-grid">
            <div class="form-field">
              <label class="form-label"
                >Nombre del Cliente <span class="form-required">*</span></label
              >
              <v-text-field
                v-model="form.client_name"
                v-validate="'required'"
                name="client_name"
                placeholder="Farmacia Central"
                solo
                class="form-input"
                :class="{ 'form-input--locked': invoicePicker.locked }"
                :readonly="invoicePicker.locked"
                :error-messages="errors.collect('client_name')"
              ></v-text-field>
            </div>
            <div class="form-field">
              <label class="form-label">Teléfono</label>
              <v-text-field
                v-model="form.client_phone"
                name="client_phone"
                placeholder="+573001234567"
                solo
                class="form-input"
                :class="{ 'form-input--locked': invoicePicker.locked }"
                :readonly="invoicePicker.locked"
              ></v-text-field>
            </div>
            <div class="form-field" v-if="assignmentType === 'transportista'">
              <label class="form-label">Correo Electrónico</label>
              <v-text-field
                v-model="form.client_email"
                v-validate="'email'"
                name="client_email"
                placeholder="cliente@correo.com"
                solo
                class="form-input"
                :class="{ 'form-input--locked': invoicePicker.locked }"
                :readonly="invoicePicker.locked"
                :error-messages="errors.collect('client_email')"
              ></v-text-field>
            </div>
          </div>
        </section>

        <!-- ===== Asignación (opcional) ===== -->
        <div class="form-divider"></div>
        <section class="form-section">
          <h3 class="form-section__title">Asignación</h3>
          <div class="form-grid">
            <div class="form-field form-field--full">
              <label class="form-label">{{ assigneeLabel }} (opcional)</label>
              <v-select
                v-model="form.assigned_user_id"
                :items="courierItems"
                :loading="loadingCouriers"
                clearable
                solo
                class="form-input"
                :no-data-text="`Sin ${assigneeLabelLower}s disponibles`"
                :placeholder="
                  `Selecciona un ${assigneeLabelLower} (o asígnalo después)`
                "
              ></v-select>
              <p
                class="form-hint"
                style="margin:6px 0 0;font-size:11.5px;color:#94a3b8"
              >
                Si lo dejas vacío, podrás asignarlo después desde el detalle del
                servicio.
              </p>
            </div>
          </div>
        </section>

        <div class="form-divider"></div>

        <!-- ===== Origen y Destino ===== -->
        <section class="form-section">
          <h3 class="form-section__title">Origen y Destino</h3>

          <p class="form-subsection">Recogida</p>
          <div class="form-grid">
            <div class="form-field form-field--full">
              <label class="form-label">
                Dirección de recogida <span class="form-required">*</span>
              </label>
              <v-text-field
                v-model="form.origin.address"
                v-validate="'required'"
                name="origin_address"
                placeholder="Calle 10 #45-20, Ciudad"
                solo
                class="form-input"
                :error-messages="errors.collect('origin_address')"
              ></v-text-field>
            </div>
            <template v-if="assignmentType === 'transportista'">
              <div class="form-field">
                <label class="form-label">Persona de Contacto</label>
                <v-text-field
                  v-model="form.origin.contact_name"
                  name="origin_contact_name"
                  placeholder="Nombre del contacto"
                  solo
                  class="form-input"
                ></v-text-field>
              </div>
              <div class="form-field">
                <label class="form-label">Teléfono de Contacto</label>
                <v-text-field
                  v-model="form.origin.contact_phone"
                  name="origin_contact_phone"
                  placeholder="+573001234567"
                  solo
                  class="form-input"
                ></v-text-field>
              </div>
            </template>
          </div>

          <p class="form-subsection form-subsection--dest">Entrega</p>
          <div class="form-grid">
            <div
              class="form-field"
              :class="{ 'form-field--full': assignmentType === 'mensajero' }"
            >
              <label class="form-label"
                >Dirección de entrega
                <span class="form-required">*</span></label
              >
              <v-text-field
                v-model="form.destination.address"
                v-validate="'required'"
                name="destination_address"
                placeholder="Carrera 15 #80-30, Ciudad"
                solo
                class="form-input"
                :error-messages="errors.collect('destination_address')"
              ></v-text-field>
            </div>
            <template v-if="assignmentType === 'transportista'">
              <div class="form-field">
                <label class="form-label">Persona de Contacto</label>
                <v-text-field
                  v-model="form.destination.contact_name"
                  name="destination_contact_name"
                  placeholder="Nombre del contacto"
                  solo
                  class="form-input"
                ></v-text-field>
              </div>
              <div class="form-field">
                <label class="form-label">Teléfono de Contacto</label>
                <v-text-field
                  v-model="form.destination.contact_phone"
                  name="destination_contact_phone"
                  placeholder="+573001234567"
                  solo
                  class="form-input"
                ></v-text-field>
              </div>
            </template>
          </div>

          <!-- Búsqueda de ubicación Google Places -->
          <div class="map-search-row" style="margin-top: 20px;">
            <div class="map-search-group">
              <div class="map-search-label">
                <span class="map-dot map-dot--origin map-dot--inline"></span>
                <span class="map-search-label-text">Buscar Origen</span>
              </div>
              <div class="map-search-input-wrap">
                <span class="map-search-icon">&#9906;</span>
                <gmap-autocomplete
                  class="map-search-input"
                  placeholder="Escribe una dirección de origen..."
                  :options="{
                    fields: ['geometry', 'formatted_address', 'name'],
                  }"
                  @place_changed="onOriginPlaceChanged"
                ></gmap-autocomplete>
              </div>
            </div>
            <div class="map-search-group">
              <div class="map-search-label">
                <span class="map-dot map-dot--dest map-dot--inline"></span>
                <span class="map-search-label-text">Buscar Destino</span>
              </div>
              <div class="map-search-input-wrap">
                <span class="map-search-icon">&#9906;</span>
                <gmap-autocomplete
                  class="map-search-input"
                  placeholder="Escribe una dirección de destino..."
                  :options="{
                    fields: ['geometry', 'formatted_address', 'name'],
                  }"
                  @place_changed="onDestPlaceChanged"
                ></gmap-autocomplete>
              </div>
            </div>
          </div>

          <!-- Mapa Google Maps -->
          <div style="margin-top: 12px;">
            <div class="map-legend">
              <span class="map-dot map-dot--origin"></span>
              <span class="map-legend-label">Origen</span>
              <span class="map-dot map-dot--dest"></span>
              <span class="map-legend-label">Destino</span>
              <span class="map-coords">
                Origen: {{ form.origin.latitude.toFixed(5) }},
                {{ form.origin.longitude.toFixed(5) }} &nbsp;·&nbsp; Destino:
                {{ form.destination.latitude.toFixed(5) }},
                {{ form.destination.longitude.toFixed(5) }}
              </span>
            </div>
            <div class="map-wrapper">
              <gmap-map
                ref="gmap"
                :center="mapCenter"
                :zoom="mapZoom"
                style="height: 420px; width: 100%;"
                :options="{
                  mapTypeControl: false,
                  streetViewControl: false,
                  fullscreenControl: true,
                  gestureHandling: 'greedy',
                }"
              >
                <gmap-marker
                  :position="originPosition"
                  :draggable="true"
                  :icon="gmapOriginIcon"
                  @dragend="onOriginDragEnd"
                ></gmap-marker>
                <gmap-marker
                  :position="destPosition"
                  :draggable="true"
                  :icon="gmapDestIcon"
                  @dragend="onDestDragEnd"
                ></gmap-marker>
                <gmap-polyline
                  v-if="routeCoords.length"
                  :path="routeCoords"
                  :options="{
                    strokeColor: '#254F87',
                    strokeWeight: 5,
                    strokeOpacity: 0.8,
                  }"
                ></gmap-polyline>
              </gmap-map>
            </div>
            <div v-if="routeDistance" class="route-info-bar">
              <span class="route-info-item">
                <v-icon
                  small
                  style="color:#254F87;vertical-align:middle;margin-right:4px"
                  >straighten</v-icon
                >
                <strong>{{ routeDistance.km }}</strong>
              </span>
              <span class="route-info-sep">·</span>
              <span class="route-info-item">
                <v-icon
                  small
                  style="color:#254F87;vertical-align:middle;margin-right:4px"
                  >access_time</v-icon
                >
                <strong>{{ routeDistance.dur }}</strong> en auto
              </span>
              <span v-if="routeLoading" class="route-info-loading"
                >Calculando...</span
              >
            </div>
            <p class="map-hint">
              Arrastra los marcadores para ajustar la ruta.
              <strong style="color:#254F87">Azul</strong> = Origen &nbsp;·&nbsp;
              <strong style="color:#E5252A">Rojo</strong> = Destino.
            </p>
          </div>
        </section>

        <!-- ===== Footer ===== -->
        <div class="form-footer">
          <v-btn class="btn-cancel" flat @click="$router.push(logisticsHome())">
            <v-icon left small>close</v-icon>Cancelar
          </v-btn>
          <v-btn type="submit" class="btn-save" :loading="saving">
            <v-icon left small>save</v-icon>
            Guardar Servicio
          </v-btn>
        </div>
      </v-form>
    </div>

    <!-- ===== Invoice Picker Dialog ===== -->
    <v-dialog max-width="960" v-model="invoicePicker.show" scrollable>
      <v-card class="modal-card">
        <div class="ip-dlg-header">
          <div>
            <h3 class="ip-dlg-title">
              <v-icon small class="ip-dlg-title__icon"
                >assignment_turned_in</v-icon
              >
              Facturas disponibles para entrega
            </h3>
            <p class="ip-dlg-subtitle">
              Selecciona una factura para autocompletar el formulario
            </p>
          </div>
          <v-btn icon small @click="invoicePicker.show = false">
            <v-icon small>close</v-icon>
          </v-btn>
        </div>

        <div class="ip-search-wrap">
          <div class="ip-search-row">
            <div class="ip-search-box">
              <v-icon small class="ip-search-box__icon">receipt</v-icon>
              <input
                v-model="invoicePicker.term"
                class="ip-search-box__input"
                placeholder="N° Factura, control, orden..."
                @input="onInvoiceSearch"
              />
              <span
                v-if="invoicePicker.loading && invoicePicker.term"
                class="ip-search-spinner"
              ></span>
            </div>
            <div class="ip-search-box ip-search-box--select">
              <v-icon small class="ip-search-box__icon">business</v-icon>
              <select
                v-model="invoicePicker.distId"
                class="ip-search-box__select"
                @change="onDistChange"
              >
                <option :value="null">Todos los distribuidores</option>
                <option
                  v-for="d in invoicePicker.distributors"
                  :key="d.id"
                  :value="d.id"
                  >{{ d.name }}</option
                >
              </select>
              <span
                v-if="invoicePicker.loadingDist"
                class="ip-search-spinner"
              ></span>
            </div>
          </div>
        </div>

        <v-card-text class="ip-body">
          <div
            v-if="invoicePicker.loading && !invoicePicker.items.length"
            class="ip-state-loading"
          >
            <v-progress-circular
              indeterminate
              color="#254F87"
              size="32"
            ></v-progress-circular>
            <span>Cargando facturas...</span>
          </div>

          <template v-else-if="invoicePicker.items.length">
            <!-- Desktop table -->
            <div class="ip-table-wrap">
              <table class="ip-table" cellspacing="0">
                <thead>
                  <tr>
                    <th>N° Factura</th>
                    <th>N° Control</th>
                    <th>Distribuidor</th>
                    <th>Laboratorio</th>
                    <th>Ingreso</th>
                    <th>Salida</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="inv in invoicePicker.items"
                    :key="inv.id"
                    class="ip-row"
                    @click="selectInvoice(inv)"
                  >
                    <td>
                      <strong>{{ (inv.invoiceNumber || '').trim() }}</strong>
                    </td>
                    <td>{{ inv.controlNumber }}</td>
                    <td>
                      {{ inv.dist_invoices ? inv.dist_invoices.name : '—' }}
                    </td>
                    <td class="ip-col-lab">
                      {{ inv.lab_invoices ? inv.lab_invoices.name : '—' }}
                    </td>
                    <td>{{ inv.admissionDate }}</td>
                    <td>{{ inv.departureDate || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Mobile cards -->
            <div class="ip-cards">
              <div
                v-for="inv in invoicePicker.items"
                :key="'m-' + inv.id"
                class="ip-card"
                @click="selectInvoice(inv)"
              >
                <p class="ip-card__invoice">
                  {{ (inv.invoiceNumber || '').trim() }}
                </p>
                <p class="ip-card__control">Control: {{ inv.controlNumber }}</p>
                <p class="ip-card__dist">
                  {{ inv.dist_invoices ? inv.dist_invoices.name : '—' }}
                </p>
                <p class="ip-card__meta">
                  {{ inv.admissionDate }} ·
                  {{ inv.lab_invoices ? inv.lab_invoices.name : '' }}
                </p>
              </div>
            </div>

            <div v-if="invoicePicker.totalPages > 1" class="ip-pagination">
              <v-btn
                icon
                small
                :disabled="invoicePicker.page <= 1 || invoicePicker.loading"
                @click="goToPage(invoicePicker.page - 1)"
              >
                <v-icon small>chevron_left</v-icon>
              </v-btn>
              <span class="ip-pagination__info">
                Página {{ invoicePicker.page }} / {{ invoicePicker.totalPages }}
                <span class="ip-pagination__total"
                  >({{ invoicePicker.total }} resultados)</span
                >
              </span>
              <v-btn
                icon
                small
                :disabled="
                  invoicePicker.page >= invoicePicker.totalPages ||
                    invoicePicker.loading
                "
                @click="goToPage(invoicePicker.page + 1)"
              >
                <v-icon small>chevron_right</v-icon>
              </v-btn>
            </div>
            <div
              v-else-if="invoicePicker.total > 0"
              class="ip-pagination ip-pagination--total"
            >
              {{ invoicePicker.total }} resultado{{
                invoicePicker.total !== 1 ? 's' : ''
              }}
            </div>
          </template>

          <div v-else class="ip-state-empty">
            <v-icon class="ip-state-empty__icon">find_in_page</v-icon>
            <p class="ip-state-empty__text">No se encontraron facturas</p>
          </div>
        </v-card-text>

        <div class="modal-footer">
          <v-btn
            class="btn-cancel"
            flat
            small
            @click="invoicePicker.show = false"
          >
            <v-icon left small>close</v-icon>Cerrar
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Dialog de mensaje -->
    <v-dialog max-width="500" v-if="dialog.message" v-model="dialog.show">
      <v-card class="modal-card">
        <div
          class="modal-header"
          :class="
            dialog.color === 'success'
              ? 'modal-header--success'
              : 'modal-header--error'
          "
        >
          <v-icon class="modal-header__icon">{{
            dialog.color === 'success' ? 'check_circle' : 'error'
          }}</v-icon>
          <h3 class="modal-header__title">
            {{ dialog.color === 'success' ? 'Servicio creado' : 'Error' }}
          </h3>
        </div>
        <v-card-text class="modal-body">
          <p>{{ dialog.message }}</p>
        </v-card-text>
        <div class="modal-footer">
          <v-btn class="btn-save" small @click="onDialogClose">Aceptar</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import Api from '@/api/index'

// Directiva click-outside inline
const ClickOutside = {
  bind(el, binding) {
    el.__clickOutsideHandler__ = e => {
      if (!el.contains(e.target)) binding.value(e)
    }
    document.addEventListener('mousedown', el.__clickOutsideHandler__)
  },
  unbind(el) {
    document.removeEventListener('mousedown', el.__clickOutsideHandler__)
  },
}

export default {
  name: 'EnvioForm',
  directives: { ClickOutside },
  $_veeValidate: { validator: 'new' },
  data() {
    return {
      saving: false,
      assignmentType:
        this.$route.query.tipo === 'mensajero' ? 'mensajero' : 'transportista',
      templates: [],
      loadingTemplates: false,
      templateError: '',
      couriers: [],
      loadingCouriers: false,
      rolesCatalog: [],
      dialog: { show: false, message: '', color: '' },
      invoicePicker: {
        show: false,
        loading: false,
        loadingDist: false,
        locked: false,
        items: [],
        term: '',
        distId: null,
        distributors: [],
        page: 1,
        rpp: 15,
        total: 0,
        totalPages: 0,
      },
      refHoverTimer: null,
      searchTimer: null,
      mapCenter: { lat: 8.9936, lng: -79.5197 },
      mapZoom: 13,
      originPosition: { lat: 8.9936, lng: -79.5197 },
      destPosition: { lat: 8.98, lng: -79.505 },
      originLatLng: [8.9936, -79.5197],
      destLatLng: [8.98, -79.505],
      originSearch: '',
      destSearch: '',
      originSearching: false,
      destSearching: false,
      originSearchError: '',
      destSearchError: '',
      originSuggestions: [],
      destSuggestions: [],
      originOpen: false,
      destOpen: false,
      originTimer: null,
      destTimer: null,
      routeCoords: [],
      routeInfo: null,
      routeLoading: false,
      priorityOptions: [
        { text: 'Baja', value: 'baja' },
        { text: 'Media', value: 'media' },
        { text: 'Alta', value: 'alta' },
      ],
      form: {
        distributor_id: null,
        bitacora_template_id: null,
        reference_number: '',
        invoice_number: '',
        client_name: '',
        client_phone: '',
        client_email: '',
        description: '',
        weight: null,
        volume: null,
        entry_date: new Date().toISOString().slice(0, 10),
        estimated_delivery_date: '',
        priority: 'media',
        observations: '',
        assigned_user_id: null,
        origin: {
          name: '',
          address: '',
          contact_name: '',
          contact_phone: '',
          latitude: 8.9936,
          longitude: -79.5197,
        },
        destination: {
          name: '',
          address: '',
          contact_name: '',
          contact_phone: '',
          latitude: 8.98,
          longitude: -79.505,
        },
      },
    }
  },
  computed: {
    templateItems() {
      return this.templates.map(t => ({ text: t.name, value: t.id }))
    },
    selectedTemplate() {
      return this.templates.find(t => t.id === this.form.bitacora_template_id)
    },
    gmapOriginIcon() {
      return {
        path:
          'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
        fillColor: '#254F87',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
        scale: 1.8,
        anchor: { x: 12, y: 22 },
      }
    },
    gmapDestIcon() {
      return {
        path:
          'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
        fillColor: '#E5252A',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
        scale: 1.8,
        anchor: { x: 12, y: 22 },
      }
    },
    routeDistance() {
      if (!this.routeInfo) return null
      return {
        km:
          this.routeInfo.distanceText ||
          `${(this.routeInfo.distance / 1000).toFixed(1)} km`,
        dur:
          this.routeInfo.durationText ||
          `${Math.round(this.routeInfo.duration / 60)} min`,
      }
    },
    courierItems() {
      return this.couriers.map(u => ({
        text: `${u.firstName} ${u.lastName}`,
        value: u.id,
      }))
    },
    assigneeLabel() {
      return this.assignmentType === 'mensajero' ? 'Mensajero' : 'Transportista'
    },
    assigneeLabelLower() {
      return this.assignmentType === 'mensajero' ? 'mensajero' : 'transportista'
    },
  },
  watch: {
    assignmentType() {
      this.form.bitacora_template_id = null
      this.loadTemplates()
      // Al cambiar de tipo, limpiar la selección y traer el rol correcto
      this.form.assigned_user_id = null
      this.couriers = []
      this.loadCouriers()
    },
  },
  created() {
    this.loadTemplates()
    this.loadCouriers()
    this.fetchDistributors()
  },
  methods: {
    logisticsHome() {
      return this.assignmentType === 'transportista'
        ? '/logistica/transportista'
        : '/logistica/mensajeria'
    },
    async loadTemplates() {
      const type = this.assignmentType
      this.loadingTemplates = true
      this.templateError = ''
      try {
        const rows = await Api.BitacoraTemplate.getAll({
          assignment_type: type,
          enabled: true,
        })
        if (!Array.isArray(rows))
          throw new Error('No se pudieron cargar las bitácoras.')
        if (type !== this.assignmentType) return
        this.templates = rows
        if (rows.length === 1) this.form.bitacora_template_id = rows[0].id
        if (!rows.length)
          this.templateError =
            'No hay bitácoras activas. Solicite su configuración al administrador.'
      } catch (e) {
        this.templateError = e.message
      } finally {
        if (type === this.assignmentType) this.loadingTemplates = false
      }
    },
    ...mapActions('shipments', ['create']),
    async resolveRoleId({ code, nameHint, fallback }) {
      if (!this.rolesCatalog.length) {
        const res = await Api.User.getRoles()
        let list = []
        if (Array.isArray(res)) list = res
        else if (res && Array.isArray(res.items)) list = res.items
        else if (res && res.data)
          list = Array.isArray(res.data) ? res.data : res.data.items || []
        this.rolesCatalog = list
      }
      const up = s => (s || '').toString().toUpperCase()
      const match = this.rolesCatalog.find(
        r => up(r.code) === code || (nameHint && up(r.name).includes(nameHint))
      )
      return match ? match.id : fallback
    },
    async loadCouriers() {
      this.loadingCouriers = true
      try {
        // El rol a traer depende del tipo de servicio seleccionado
        const cfg =
          this.assignmentType === 'mensajero'
            ? { code: 'MENSAJERO', nameHint: 'MENSAJER', fallback: null }
            : { code: 'TRA', nameHint: 'TRANSPORT', fallback: null }
        const roleId = await this.resolveRoleId(cfg)
        const params = { rpp: 100, page: 1 }
        if (roleId != null) params.roleId = roleId
        const res = await Api.User.getAll(params)
        this.couriers = res
          ? Array.isArray(res)
            ? res
            : res.items || res.data || []
          : []
      } catch (e) {
        /* silent */
      } finally {
        this.loadingCouriers = false
      }
    },
    onRefLabelHover() {
      this.refHoverTimer = setTimeout(() => {
        this.openInvoicePicker()
      }, 350)
    },
    onRefLabelLeave() {
      clearTimeout(this.refHoverTimer)
    },
    openInvoicePicker() {
      this.invoicePicker.show = true
      this.invoicePicker.page = 1
      this.invoicePicker.items = []
      this.invoicePicker.term = ''
      this.invoicePicker.distId = null
      this.fetchTransitInvoices()
      if (!this.invoicePicker.distributors.length) this.fetchDistributors()
    },
    async fetchDistributors() {
      this.invoicePicker.loadingDist = true
      try {
        const result = await Api.Distributor.getAll({ rpp: 200, page: 1 })
        this.invoicePicker.distributors = (result && result.items) || []
      } catch (e) {
        /* silent */
      } finally {
        this.invoicePicker.loadingDist = false
      }
    },
    async fetchTransitInvoices() {
      this.invoicePicker.loading = true
      try {
        const p = this.invoicePicker
        const params = { rpp: p.rpp, page: p.page, status: 'deliver' }
        if (p.term.trim()) params.term = p.term.trim()
        if (p.distId) params.distId = p.distId
        const result = await Api.Invoice.getTransitList(params)
        this.invoicePicker.items = (result && result.items) || []
        this.invoicePicker.total = (result && result.total) || 0
        this.invoicePicker.totalPages =
          Math.ceil(this.invoicePicker.total / p.rpp) || 1
      } catch (e) {
        /* silent */
      } finally {
        this.invoicePicker.loading = false
      }
    },
    goToPage(page) {
      if (page < 1 || page > this.invoicePicker.totalPages) return
      this.invoicePicker.page = page
      this.fetchTransitInvoices()
    },
    onInvoiceSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.invoicePicker.page = 1
        this.fetchTransitInvoices()
      }, 400)
    },
    onDistChange() {
      this.invoicePicker.page = 1
      this.fetchTransitInvoices()
    },
    selectInvoice(inv) {
      this.form.distributor_id =
        inv.distId || (inv.dist_invoices && inv.dist_invoices.id) || null
      const contact = inv.dist_invoices && inv.dist_invoices.contact
      this.form.reference_number = inv.controlNumber || ''
      this.form.invoice_number = (inv.invoiceNumber || '').trim()
      this.form.client_name = contact
        ? `${contact.firstName} ${contact.lastName}`.trim()
        : inv.dist_invoices
        ? inv.dist_invoices.name
        : ''
      this.form.client_email = contact ? contact.email : ''
      this.form.client_phone = inv.dist_invoices ? inv.dist_invoices.phone : ''
      this.form.entry_date =
        inv.admissionDate || new Date().toISOString().slice(0, 10)
      this.form.estimated_delivery_date = inv.departureDate || ''
      this.form.description = inv.lab_invoices ? inv.lab_invoices.name : ''
      if (inv.packageNumber)
        this.form.weight = parseFloat(inv.packageNumber) || null
      this.invoicePicker.locked = true
      this.invoicePicker.show = false
    },
    clearInvoiceSelection() {
      this.form.distributor_id = null
      this.form.reference_number = ''
      this.form.invoice_number = ''
      this.form.client_name = ''
      this.form.client_email = ''
      this.form.client_phone = ''
      this.form.entry_date = new Date().toISOString().slice(0, 10)
      this.form.estimated_delivery_date = ''
      this.form.description = ''
      this.form.weight = null
      this.invoicePicker.locked = false
    },
    async submit() {
      const valid = await this.$validator.validateAll()
      if (!valid) return
      if (!this.form.bitacora_template_id) {
        this.dialog = {
          show: true,
          message: 'Seleccione la bitácora del servicio.',
          color: 'error',
        }
        return
      }

      this.saving = true
      try {
        let payload
        if (this.assignmentType === 'mensajero') {
          // Servicio de mensajero: solo datos básicos
          payload = {
            assignment_type: 'mensajero',
            client_name: this.form.client_name,
            client_phone: this.form.client_phone,
            description: this.form.description,
            priority: 'media',
            origin: {
              name: this.form.origin.name,
              address: this.form.origin.address,
              latitude: this.form.origin.latitude,
              longitude: this.form.origin.longitude,
            },
            destination: {
              name: this.form.destination.name,
              address: this.form.destination.address,
              latitude: this.form.destination.latitude,
              longitude: this.form.destination.longitude,
            },
          }
          // Asignación opcional al crear: solo si se eligió un mensajero
          if (this.form.assigned_user_id) {
            payload.assigned_user_id = this.form.assigned_user_id
          }
        } else {
          // Servicio de transportista: formulario completo
          payload = {
            assignment_type: 'transportista',
            ...this.form,
            weight:
              this.form.weight != null ? parseFloat(this.form.weight) : null,
            volume:
              this.form.volume != null ? parseFloat(this.form.volume) : null,
          }
        }
        payload.bitacora_template_id = this.form.bitacora_template_id
        payload.distributor_id = this.form.distributor_id
        const response = await this.create(payload)

        if (response && (response.status === 200 || response.status === 201)) {
          this.dialog = {
            show: true,
            message: 'El servicio fue creado exitosamente.',
            color: 'success',
          }
        } else {
          const msg =
            (response && response.data && response.data.message) ||
            'No se pudo crear el servicio.'
          this.dialog = { show: true, message: msg, color: 'error' }
        }
      } catch (e) {
        this.dialog = {
          show: true,
          message: 'Error inesperado. Intenta de nuevo.',
          color: 'error',
        }
      } finally {
        this.saving = false
      }
    },
    closeOriginDropdown() {
      this.originOpen = false
    },
    closeDestDropdown() {
      this.destOpen = false
    },
    selectFirst(type) {
      if (type === 'origin' && this.originSuggestions.length)
        this.selectOriginSuggestion(this.originSuggestions[0])
      if (type === 'dest' && this.destSuggestions.length)
        this.selectDestSuggestion(this.destSuggestions[0])
    },
    selectOriginSuggestion(item) {
      const lat = parseFloat(item.lat)
      const lng = parseFloat(item.lon)
      this.originLatLng = [lat, lng]
      this.form.origin.latitude = lat
      this.form.origin.longitude = lng
      this.mapCenter = [lat, lng]
      this.originSearch = item.display_name
      if (!this.form.origin.address)
        this.form.origin.address = item.display_name
      this.originSuggestions = []
      this.originOpen = false
      this.fetchRoute()
    },
    selectDestSuggestion(item) {
      const lat = parseFloat(item.lat)
      const lng = parseFloat(item.lon)
      this.destLatLng = [lat, lng]
      this.form.destination.latitude = lat
      this.form.destination.longitude = lng
      this.mapCenter = [lat, lng]
      this.destSearch = item.display_name
      if (!this.form.destination.address)
        this.form.destination.address = item.display_name
      this.destSuggestions = []
      this.destOpen = false
      this.fetchRoute()
    },
    onOriginInput() {
      clearTimeout(this.originTimer)
      this.originSearchError = ''
      if (!this.originSearch.trim() || this.originSearch.length < 3) {
        this.originSuggestions = []
        this.originOpen = false
        return
      }
      this.originSearching = true
      this.originTimer = setTimeout(async () => {
        try {
          const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
            this.originSearch
          )}&format=json&limit=5&addressdetails=1`
          const res = await fetch(url, { headers: { 'Accept-Language': 'es' } })
          const data = await res.json()
          this.originSuggestions = data || []
          this.originOpen = this.originSuggestions.length > 0
          if (!this.originSuggestions.length)
            this.originSearchError = 'Sin resultados'
        } catch (e) {
          this.originSearchError = 'Error al buscar'
        } finally {
          this.originSearching = false
        }
      }, 450)
    },
    onDestInput() {
      clearTimeout(this.destTimer)
      this.destSearchError = ''
      if (!this.destSearch.trim() || this.destSearch.length < 3) {
        this.destSuggestions = []
        this.destOpen = false
        return
      }
      this.destSearching = true
      this.destTimer = setTimeout(async () => {
        try {
          const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
            this.destSearch
          )}&format=json&limit=5&addressdetails=1`
          const res = await fetch(url, { headers: { 'Accept-Language': 'es' } })
          const data = await res.json()
          this.destSuggestions = data || []
          this.destOpen = this.destSuggestions.length > 0
          if (!this.destSuggestions.length)
            this.destSearchError = 'Sin resultados'
        } catch (e) {
          this.destSearchError = 'Error al buscar'
        } finally {
          this.destSearching = false
        }
      }, 450)
    },
    onOriginPlaceChanged(place) {
      if (!place.geometry) return
      const lat = place.geometry.location.lat()
      const lng = place.geometry.location.lng()
      this.originPosition = { lat, lng }
      this.originLatLng = [lat, lng]
      this.form.origin.latitude = lat
      this.form.origin.longitude = lng
      this.mapCenter = { lat, lng }
      if (!this.form.origin.address)
        this.form.origin.address = place.formatted_address || place.name || ''
      this.fetchRoute()
    },
    onDestPlaceChanged(place) {
      if (!place.geometry) return
      const lat = place.geometry.location.lat()
      const lng = place.geometry.location.lng()
      this.destPosition = { lat, lng }
      this.destLatLng = [lat, lng]
      this.form.destination.latitude = lat
      this.form.destination.longitude = lng
      this.mapCenter = { lat, lng }
      if (!this.form.destination.address)
        this.form.destination.address =
          place.formatted_address || place.name || ''
      this.fetchRoute()
    },
    async onOriginDragEnd(event) {
      const lat = event.latLng.lat()
      const lng = event.latLng.lng()
      this.originPosition = { lat, lng }
      this.originLatLng = [lat, lng]
      this.form.origin.latitude = lat
      this.form.origin.longitude = lng
      this.form.origin.address = await this.reverseGeocode(lat, lng)
      this.fetchRoute()
    },
    async onDestDragEnd(event) {
      const lat = event.latLng.lat()
      const lng = event.latLng.lng()
      this.destPosition = { lat, lng }
      this.destLatLng = [lat, lng]
      this.form.destination.latitude = lat
      this.form.destination.longitude = lng
      this.form.destination.address = await this.reverseGeocode(lat, lng)
      this.fetchRoute()
    },
    async reverseGeocode(lat, lng) {
      try {
        const google = await this.$gmapApiPromiseLazy()
        const geocoder = new google.maps.Geocoder()
        const result = await new Promise((resolve, reject) => {
          geocoder.geocode({ location: { lat, lng } }, (res, status) => {
            if (status === 'OK' && res.length) resolve(res)
            else reject(status)
          })
        })
        return result[0].formatted_address || ''
      } catch (e) {
        return ''
      }
    },
    onDialogClose() {
      this.dialog.show = false
      if (this.dialog.color === 'success') {
        this.$router.push(this.logisticsHome())
      }
    },
    async fetchRoute() {
      this.routeLoading = true
      this.routeCoords = []
      this.routeInfo = null
      try {
        const google = await this.$gmapApiPromiseLazy()
        const directionsService = new google.maps.DirectionsService()
        const result = await new Promise((resolve, reject) => {
          directionsService.route(
            {
              origin: this.originPosition,
              destination: this.destPosition,
              travelMode: google.maps.TravelMode.DRIVING,
            },
            (res, status) => {
              if (status === 'OK') resolve(res)
              else reject(status)
            }
          )
        })
        const leg = result.routes[0].legs[0]
        this.routeInfo = {
          distance: leg.distance.value,
          duration: leg.duration.value,
          distanceText: leg.distance.text,
          durationText: leg.duration.text,
        }
        const path = result.routes[0].overview_path
        this.routeCoords = path.map(p => ({ lat: p.lat(), lng: p.lng() }))
      } catch (e) {
        /* ignore */
      } finally {
        this.routeLoading = false
      }
    },
  },
}
</script>

<style scoped>
.envio-form-page {
  padding: 24px 28px 40px;
  color: #172133;
  background: #f9fafb;
  min-height: 100%;
}

/* ── Hero ── */
.form-hero {
  margin-bottom: 24px;
}

.form-crumb {
  margin: 0 0 6px;
  font-size: 12.5px;
  color: #94a3b8;
  letter-spacing: 0.02em;
}

.form-title {
  margin: 0 0 6px;
  font-size: 26px;
  font-weight: 700;
  color: #172133;
  letter-spacing: -0.01em;
}

.form-subtitle {
  margin: 0;
  font-size: 13.5px;
  color: #64748b;
  max-width: 720px;
}

/* ── Card ── */
.form-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 32px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04),
    0 4px 14px rgba(15, 23, 42, 0.05);
}

/* ── Section ── */
.form-section {
  margin-bottom: 0;
}

.form-section__title {
  margin: 0 0 6px;
  font-size: 14px;
  font-weight: 700;
  color: #172133;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  position: relative;
  padding-bottom: 10px;
}

.form-section__title::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 32px;
  height: 2px;
  background: #e5252a;
  border-radius: 1px;
}

/* ── Subsection labels ── */
.form-subsection {
  margin: 20px 0 12px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #254f87;
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-subsection::before {
  content: '';
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #254f87;
  flex-shrink: 0;
}

.form-subsection--dest {
  color: #c79a2b;
}

.form-subsection--dest::before {
  background: #c79a2b;
}

/* ── Divider ── */
.form-divider {
  height: 1px;
  background: #eef2f7;
  margin: 24px 0;
}

/* ── Selector de tipo de servicio ── */
.type-toggle {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-top: 16px;
}

.type-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 18px 16px;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: center;
}

.type-card:hover {
  border-color: #c7d9f5;
  background: #fafcff;
}

.type-card--active {
  border-color: #254f87;
  background: #f0f6ff;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.1);
}

.type-card__icon {
  color: #94a3b8 !important;
  font-size: 28px !important;
}

.type-card--active .type-card__icon {
  color: #254f87 !important;
}

.type-card__label {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
}

.type-card__hint {
  font-size: 11.5px;
  color: #94a3b8;
}

.type-card--active .type-card__label {
  color: #254f87;
}

/* ── Grid ── */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 18px;
  margin-top: 16px;
}

.form-grid--3 {
  grid-template-columns: 1fr 1fr 1fr;
}

.form-field--full {
  grid-column: 1 / -1;
}

.form-field {
  display: flex;
  flex-direction: column;
}

/* ── Labels ── */
.form-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
  letter-spacing: 0.01em;
}

.form-required {
  color: #e5252a;
  font-weight: 700;
}

/* ── Footer ── */
.form-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid #eef2f7;
}

.btn-cancel {
  border: 1px solid #e2e8f0 !important;
  background: transparent !important;
  color: #475569 !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  height: 42px !important;
  padding: 0 18px !important;
  box-shadow: none !important;
}

.btn-cancel:hover {
  background: #f8fafc !important;
  color: #172133 !important;
  border-color: #cbd5e1 !important;
}

.btn-save {
  background: #e5252a !important;
  color: #ffffff !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  height: 42px !important;
  padding: 0 22px !important;
  box-shadow: 0 1px 2px rgba(229, 37, 42, 0.12),
    0 4px 12px rgba(229, 37, 42, 0.2) !important;
  transition: all 0.15s ease !important;
}

.btn-save:hover {
  background: #c91e23 !important;
  box-shadow: 0 2px 4px rgba(229, 37, 42, 0.18),
    0 6px 16px rgba(229, 37, 42, 0.28) !important;
  transform: translateY(-1px);
}

/* ── Modal ── */
.modal-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #eef2f7;
}

.modal-header--success .modal-header__icon {
  color: #16a34a;
  font-size: 24px !important;
}
.modal-header--error .modal-header__icon {
  color: #e5252a;
  font-size: 24px !important;
}

.modal-header__title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #172133;
}

.modal-body {
  padding: 14px 20px !important;
  color: #334155;
  font-size: 14px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 12px 20px 16px;
  border-top: 1px solid #eef2f7;
}

/* ── Mapa ── */
.map-legend {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.map-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: inline-block;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
  margin-left: 8px;
}

.map-dot:first-child {
  margin-left: 0;
}
.map-dot--origin {
  background: #254f87;
}
.map-dot--dest {
  background: #e5252a;
}

.map-legend-label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  margin-right: 4px;
}

.map-coords {
  font-size: 11px;
  color: #94a3b8;
  margin-left: auto;
}

.map-wrapper {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
}

.map-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #94a3b8;
}

/* ── Ruta ── */
.route-info-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  background: #f0f6ff;
  border: 1px solid #c7d9f5;
  border-radius: 8px;
  padding: 10px 16px;
  margin-top: 10px;
}

.route-info-item {
  font-size: 13px;
  color: #1e3a5f;
}

.route-info-sep {
  color: #94a3b8;
  font-size: 16px;
}

.route-info-loading {
  margin-left: auto;
  font-size: 12px;
  color: #94a3b8;
  font-style: italic;
}

/* ── Autocomplete búsqueda ── */
.map-search-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.map-search-group {
  flex: 1;
  min-width: 220px;
}

.map-search-label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 5px;
}

.map-search-label-text {
  font-size: 12.5px;
  font-weight: 600;
  color: #334155;
  letter-spacing: 0.01em;
}

.map-dot--inline {
  display: inline-block;
}

.map-search-input-wrap {
  display: flex;
  align-items: center;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  transition: border-color 0.15s;
}

.map-search-input-wrap:focus-within {
  border-color: #254f87;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.1);
}

.map-search-icon {
  padding: 0 8px 0 12px;
  font-size: 15px;
  color: #94a3b8;
  user-select: none;
}

.map-search-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 10px 12px 10px 0;
  font-size: 13px;
  color: #1e293b;
  background: transparent;
}

.map-search-input::placeholder {
  color: #94a3b8;
}

@media (max-width: 768px) {
  .envio-form-page {
    padding: 16px 16px 32px;
  }
  .form-card {
    padding: 20px 16px;
  }
  .form-grid,
  .form-grid--3 {
    grid-template-columns: 1fr;
  }
  .form-field--full {
    grid-column: 1;
  }
  .form-footer {
    flex-direction: column;
  }
  .btn-cancel,
  .btn-save {
    width: 100%;
  }
  .map-coords {
    display: none;
  }
}
</style>

<style>
.envio-form-page
  .form-input.v-text-field.v-text-field--solo
  .v-input__control
  > .v-input__slot,
.envio-form-page
  .form-input.v-textarea.v-text-field--solo
  .v-input__control
  > .v-input__slot,
.envio-form-page
  .form-input.v-select.v-text-field--solo
  .v-input__control
  > .v-input__slot {
  background: #ffffff !important;
  border: 1px solid #d1d5db !important;
  border-radius: 10px !important;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04) !important;
  min-height: 44px !important;
  padding: 0 14px !important;
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    background 0.18s ease;
}

.envio-form-page
  .form-input.v-text-field.v-text-field--solo:hover
  .v-input__control
  > .v-input__slot,
.envio-form-page
  .form-input.v-textarea.v-text-field--solo:hover
  .v-input__control
  > .v-input__slot,
.envio-form-page
  .form-input.v-select.v-text-field--solo:hover
  .v-input__control
  > .v-input__slot {
  border-color: #9ca3af !important;
}

.envio-form-page
  .form-input.v-text-field.v-text-field--solo.v-input--is-focused
  .v-input__control
  > .v-input__slot,
.envio-form-page
  .form-input.v-textarea.v-text-field--solo.v-input--is-focused
  .v-input__control
  > .v-input__slot,
.envio-form-page
  .form-input.v-select.v-text-field--solo.v-input--is-focused
  .v-input__control
  > .v-input__slot {
  border-color: #254f87 !important;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.12),
    0 1px 2px rgba(15, 23, 42, 0.04) !important;
}

.envio-form-page .form-input.error--text .v-input__control > .v-input__slot {
  border-color: #ef4444 !important;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12) !important;
}

.envio-form-page .form-input input,
.envio-form-page .form-input textarea {
  color: #172133 !important;
  font-size: 14px !important;
  caret-color: #172133 !important;
}

.envio-form-page .form-input input::placeholder,
.envio-form-page .form-input textarea::placeholder {
  color: #94a3b8 !important;
  opacity: 1;
}

.envio-form-page .form-input .v-input__icon .v-icon {
  color: #94a3b8 !important;
  font-size: 19px !important;
}

.envio-form-page
  .form-input--locked.v-text-field.v-text-field--solo
  .v-input__slot {
  background: #f8fafc !important;
  cursor: not-allowed !important;
}

.envio-form-page
  .form-input--locked.v-text-field.v-text-field--solo
  .v-input__slot
  input,
.envio-form-page
  .form-input--locked.v-text-field.v-text-field--solo
  .v-input__slot
  textarea {
  color: #475569 !important;
  cursor: not-allowed !important;
}

/* ── Invoice Picker trigger ── */
.ip-trigger {
  cursor: default;
  user-select: none;
  gap: 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.ip-trigger__badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: #eef4ff;
  border: 1px solid #c7d9f5;
  border-radius: 20px;
  padding: 1px 8px 1px 4px;
  font-size: 10.5px;
  font-weight: 600;
  color: #254f87;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
}

.ip-trigger__badge:hover {
  background: #dbeafe;
}

.ip-trigger__icon {
  color: #254f87 !important;
  font-size: 13px !important;
}

.ip-locked-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 20px;
  padding: 1px 8px 1px 4px;
  font-size: 10.5px;
  font-weight: 600;
  color: #16a34a;
  white-space: nowrap;
}

.ip-locked-badge__icon {
  color: #16a34a !important;
  font-size: 12px !important;
}

.ip-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 1px 7px 1px 4px;
  border-radius: 20px;
  font-size: 10.5px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  background: #eef4ff;
  border: 1px solid #c7d9f5;
  color: #254f87;
  transition: background 0.15s;
}

.ip-action-btn:hover {
  background: #dbeafe;
}

.ip-action-btn--clear {
  background: #fff5f5;
  border-color: #fecaca;
  color: #dc2626;
  padding: 1px 6px;
}

.ip-action-btn--clear:hover {
  background: #fee2e2;
}

/* ── Invoice Picker Dialog ── */
.ip-dlg-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #eef2f7;
}

.ip-dlg-title {
  margin: 0 0 3px;
  font-size: 15px;
  font-weight: 700;
  color: #172133;
  display: flex;
  align-items: center;
  gap: 6px;
}

.ip-dlg-title__icon {
  color: #254f87 !important;
  font-size: 18px !important;
}

.ip-dlg-subtitle {
  margin: 0;
  font-size: 12px;
  color: #64748b;
}

.ip-search-wrap {
  padding: 12px 20px;
  border-bottom: 1px solid #eef2f7;
  background: #fcfdff;
}

.ip-search-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.ip-search-box {
  display: flex;
  align-items: center;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  padding: 0 12px;
  transition: border-color 0.15s;
}

.ip-search-box:focus-within {
  border-color: #254f87;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.08);
}

.ip-search-box__icon {
  color: #94a3b8 !important;
  flex-shrink: 0;
  margin-right: 8px;
}

.ip-search-box__input {
  flex: 1;
  border: none;
  outline: none;
  padding: 10px 0;
  font-size: 13px;
  color: #172133;
  background: transparent;
}

.ip-search-box__input::placeholder {
  color: #94a3b8;
}

.ip-search-box--select {
  cursor: pointer;
}

.ip-search-box__select {
  flex: 1;
  border: none;
  outline: none;
  padding: 10px 0;
  font-size: 13px;
  color: #172133;
  background: transparent;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
}

.ip-search-box__select option {
  color: #172133;
}

.ip-search-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #e2e8f0;
  border-top-color: #254f87;
  border-radius: 50%;
  animation: ip-spin 0.6s linear infinite;
  flex-shrink: 0;
}

@keyframes ip-spin {
  to {
    transform: rotate(360deg);
  }
}

.ip-body {
  padding: 0 !important;
  max-height: 460px;
  overflow-y: auto;
}

.ip-state-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px 20px;
  color: #64748b;
  font-size: 13px;
}

.ip-state-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #94a3b8;
}

.ip-state-empty__icon {
  font-size: 42px !important;
  margin-bottom: 8px;
}

.ip-state-empty__text {
  margin: 0;
  font-size: 13px;
}

.ip-table-wrap {
  overflow-x: auto;
}

.ip-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.ip-table th {
  padding: 10px 14px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.ip-row {
  cursor: pointer;
  transition: background 0.1s;
}

.ip-row:hover {
  background: #f0f6ff;
}

.ip-row td {
  padding: 11px 14px;
  color: #334155;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
}

.ip-row:last-child td {
  border-bottom: 0;
}

.ip-row td strong {
  color: #172133;
  font-weight: 700;
}

.ip-col-lab {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ip-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 14px 16px;
  border-top: 1px solid #eef2f7;
}

.ip-pagination--total {
  font-size: 12px;
  color: #94a3b8;
}

.ip-pagination__info {
  font-size: 13px;
  color: #475569;
  white-space: nowrap;
}

.ip-pagination__total {
  color: #94a3b8;
  font-size: 12px;
  margin-left: 4px;
}

/* Mobile cards (hidden on desktop) */
.ip-cards {
  display: none;
}

.ip-card {
  padding: 14px 16px;
  border-bottom: 1px solid #eef2f7;
  cursor: pointer;
  transition: background 0.1s;
}

.ip-card:hover,
.ip-card:active {
  background: #f0f6ff;
}

.ip-card__invoice {
  margin: 0 0 2px;
  font-size: 14px;
  font-weight: 700;
  color: #172133;
}

.ip-card__control {
  margin: 0 0 2px;
  font-size: 12px;
  color: #64748b;
}

.ip-card__dist {
  margin: 0 0 2px;
  font-size: 12.5px;
  font-weight: 600;
  color: #334155;
}

.ip-card__meta {
  margin: 0;
  font-size: 11.5px;
  color: #94a3b8;
}

@media (max-width: 720px) {
  .ip-table-wrap {
    display: none;
  }
  .ip-cards {
    display: block;
  }
  .ip-search-row {
    grid-template-columns: 1fr;
  }
}
</style>
